/**
 * DisconnectOverlayRenderer — reconnection countdown + opponent-left menu.
 *
 * Shows during multiplayer when opponent disconnects:
 *   - 'reconnecting': countdown with "Waiting for [name]..."
 *   - 'gone': options to return to online menu or main menu
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { Keys } from 'bonkjs';
import { WORLD, MENU, Z } from '../../constants';
import type { GameState } from '../../types';

const OPTIONS = ['ONLINE MENU', 'MAIN MENU'];

function monoStyle(size: number, color: number, bold = false): TextStyle {
    return new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: size,
        fill: color,
        fontWeight: bold ? 'bold' : 'normal',
    });
}

export class DisconnectOverlayRenderer {
    private container: Container;
    private overlay: Graphics;
    private title: Text;
    private subtitle: Text;
    private countdown: Text;
    private options: Text[] = [];
    private hitRects: Graphics[] = [];
    private hint: Text;

    constructor(
        uiContainer: Container,
        tapVirtualKey: (code: string) => void,
        getState: () => GameState | null,
    ) {
        this.container = new Container();
        this.container.visible = false;
        this.container.zIndex = Z.MENU_UI + 20;
        uiContainer.addChild(this.container);

        const cx = WORLD.CANVAS_WIDTH / 2;

        // Dark overlay background
        this.overlay = new Graphics();
        this.overlay.rect(0, 0, WORLD.CANVAS_WIDTH, WORLD.CANVAS_HEIGHT);
        this.overlay.fill({ color: 0x000000, alpha: MENU.PAUSE_OVERLAY_ALPHA });
        this.overlay.eventMode = 'static';
        this.container.addChild(this.overlay);

        // Title: "CONNECTION LOST" / "OPPONENT LEFT"
        this.title = new Text({
            text: 'CONNECTION LOST',
            style: monoStyle(36, 0xff4444, true),
        });
        this.title.anchor.set(0.5);
        this.title.position.set(cx, 340);
        this.container.addChild(this.title);

        // Subtitle: "Waiting for [name]..." / "[name] left the game"
        this.subtitle = new Text({
            text: '',
            style: monoStyle(20, 0x888888),
        });
        this.subtitle.anchor.set(0.5);
        this.subtitle.position.set(cx, 390);
        this.container.addChild(this.subtitle);

        // Countdown text (large, centered)
        this.countdown = new Text({
            text: '',
            style: monoStyle(64, 0xffffff, true),
        });
        this.countdown.anchor.set(0.5);
        this.countdown.position.set(cx, 480);
        this.container.addChild(this.countdown);

        // Option texts with hit rects
        const hitW = 400;
        const hitH = 50;
        for (let i = 0; i < OPTIONS.length; i++) {
            const optY = 460 + i * 60;

            const hitRect = new Graphics();
            hitRect.position.set(cx - hitW / 2, optY - hitH / 2);
            hitRect.eventMode = 'static';
            hitRect.cursor = 'pointer';
            const optIdx = i;
            hitRect.on('pointerdown', (e: Event) => {
                e.stopPropagation();
                const s = getState();
                if (s?.multiplayer) s.multiplayer.disconnectCursor = optIdx;
                tapVirtualKey(Keys.Space);
            });
            hitRect.on('pointerover', () => {
                const s = getState();
                if (s?.multiplayer) s.multiplayer.disconnectCursor = optIdx;
            });
            this.container.addChild(hitRect);
            this.hitRects.push(hitRect);

            const opt = new Text({
                text: OPTIONS[i],
                style: monoStyle(MENU.PAUSE_OPTION_SIZE, 0xffffff),
            });
            opt.anchor.set(0.5);
            opt.position.set(cx, optY);
            this.container.addChild(opt);
            this.options.push(opt);
        }

        // Hint text
        this.hint = new Text({
            text: '',
            style: monoStyle(MENU.HINT_FONT_SIZE, MENU.HINT_COLOR),
        });
        this.hint.anchor.set(0.5);
        this.hint.position.set(cx, 460 + OPTIONS.length * 60 + 30);
        this.container.addChild(this.hint);
    }

    setVisible(v: boolean): void {
        this.container.visible = v;
    }

    update(state: GameState, elapsed: number): void {
        const mp = state.multiplayer;
        if (!mp || !mp.disconnectPhase) {
            this.container.visible = false;
            return;
        }

        this.container.visible = true;
        const opponentName = mp.opponentName || 'opponent';

        if (mp.disconnectPhase === 'reconnecting') {
            this.title.text = 'CONNECTION LOST';
            this.title.style.fill = 0xff4444;
            this.subtitle.text = `Waiting for ${opponentName}...`;
            this.subtitle.visible = true;

            // Countdown
            const remaining = Math.max(0, Math.ceil((mp.disconnectCountdownEnd - Date.now()) / 1000));
            this.countdown.text = `${remaining}s`;
            this.countdown.visible = true;
            // Pulsing alpha on countdown
            this.countdown.alpha = 0.6 + 0.4 * Math.sin(elapsed * 5);

            // Hide options
            for (const opt of this.options) opt.visible = false;
            for (const hr of this.hitRects) hr.visible = false;

            this.hint.text = 'ESC = Give up';
            this.hint.visible = true;
        } else if (mp.disconnectPhase === 'gone') {
            this.title.text = 'OPPONENT LEFT';
            this.title.style.fill = 0xff8844;
            this.subtitle.text = `${opponentName} left the game`;
            this.subtitle.visible = true;

            // Hide countdown
            this.countdown.visible = false;

            // Show options
            const hitW = 400;
            const hitH = 50;
            const r = MENU.SELECTION_CORNER_RADIUS;
            for (let i = 0; i < OPTIONS.length; i++) {
                this.options[i].visible = true;
                this.hitRects[i].visible = true;

                const isSelected = i === mp.disconnectCursor;
                this.options[i].style.fill = isSelected ? 0xffffff : 0x666666;
                this.options[i].style.fontSize = isSelected ? MENU.PAUSE_OPTION_SIZE + 4 : MENU.PAUSE_OPTION_SIZE;

                this.hitRects[i].clear();
                if (isSelected) {
                    this.hitRects[i].roundRect(0, 0, hitW, hitH, r);
                    this.hitRects[i].fill({ color: 0xffffff, alpha: MENU.SELECTION_FILL_ALPHA });
                    this.hitRects[i].roundRect(0, 0, hitW, hitH, r);
                    this.hitRects[i].stroke({ color: 0xffffff, width: 1, alpha: 0.15 });
                }
                this.hitRects[i].rect(0, 0, hitW, hitH);
                this.hitRects[i].fill({ color: 0x000000, alpha: 0.001 });

                if (isSelected) {
                    const pulse = 0.7 + 0.3 * Math.sin(elapsed * 4);
                    this.options[i].alpha = pulse;
                } else {
                    this.options[i].alpha = 1;
                }
            }

            this.hint.text = 'ESC = Main Menu';
            this.hint.visible = true;
        }
    }

    destroy(): void {
        this.container.destroy({ children: true });
    }
}
